import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useSearchParams } from 'react-router';

import Chat from '~/components/Chat';
import Loading from '~/components/Loading';
import Modal from '~/components/Modal';
import FormattedMessage from '~/components/common/FormattedMessage';

import { getPlayingStreams } from '~/modules/streams';

import StreamGuard from '../StreamGuard';
import styles from './index.scss';

const PARAM_OVERLAY_MODE = 'overlayMode';

const OverlayPrompt = ({ setShowOverlay }) => {
  const handleCancel = () => setShowOverlay(false);
  const handleAccept = () => setShowOverlay(true);

  return (
    <Modal isOpen={true} onRequestClose={handleCancel}>
      <FormattedMessage
        id="StandaloneChat_OverlayPrompt"
        defaultMessage="This chat can be used as an overlay in OBS. Would you like to enable overlay mode?"
      />
      <div>
        <button onClick={handleCancel}>
          <FormattedMessage
            id="StandaloneChat_Cancel"
            defaultMessage="Cancel"
          />
        </button>
        <button onClick={handleAccept}>
          <FormattedMessage
            id="StandaloneChat_Accept"
            defaultMessage="Enable"
          />
        </button>
      </div>
    </Modal>
  );
};

const StandaloneChat = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const [showOverlay, setShowOverlay] = useState<boolean | null>(false);
  const streams = useSelector(getPlayingStreams);

  useEffect(() => {
    const isOBS =
      typeof window !== 'undefined' &&
      window.navigator.userAgent.match(/OBS\/(\d+\.?)+/);

    const isOverlayMode =
      searchParams.has(PARAM_OVERLAY_MODE) &&
      searchParams.get(PARAM_OVERLAY_MODE) === '1';

    if (isOBS && !searchParams.has(PARAM_OVERLAY_MODE)) {
      setShowOverlay(null);
    } else {
      setShowOverlay(isOverlayMode);
    }
  }, [location]);

  const onError = () => {
    setError('Chat crashed, reloading.');
    window.setTimeout(() => setError(null), 5000);
  };

  if (showOverlay === null) {
    return <OverlayPrompt setShowOverlay={setShowOverlay} />;
  }

  return (
    <StreamGuard noSkeleton>
      <div className={styles.StandaloneChat}>
        {error || streams.length === 0 ? (
          <Loading message={error} />
        ) : (
          <Chat
            className={styles.StandaloneChat__Instance}
            onError={onError}
            overlayMode={showOverlay}
          />
        )}
      </div>
    </StreamGuard>
  );
};

export default StandaloneChat;
